import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface QueryResult<T> {
  items: T[];
  total: number;
}

/**
 * The query function receives an AbortSignal so callers can propagate
 * cancellation to the underlying network request.
 */
export type ElasticQueryFn<T> = (signal: AbortSignal) => Promise<QueryResult<T> | null>;

export interface UseElasticQueryOptions<T> {
  queryFn: ElasticQueryFn<T>;
  /**
   * Serializable values that identify this query. The hook re-fetches whenever
   * any value in this array changes — exactly like TanStack Query's queryKey.
   * Include every variable that the queryFn closes over (orgId, filters, page…).
   */
  queryKey: readonly unknown[];
  /**
   * When false the hook skips the auto-fetch and clears any existing data.
   * Useful for guarding behind permissions or missing required params.
   * @default true
   */
  enabled?: boolean;
  /**
   * Keep the previous data visible while a background refetch is in progress,
   * instead of resetting to an empty array. Mirrors TanStack Query's behaviour.
   * @default false
   */
  keepPreviousData?: boolean;
  onSuccess?: (data: T[], total: number) => void;
  onError?: (error: string) => void;
}

export interface UseElasticQueryResult<T> {
  data: T[];
  total: number;
  /** True only during the very first fetch when there is no data to display yet */
  loading: boolean;
  /** True whenever a request is in flight (including background refetches) */
  isFetching: boolean;
  error: string | null;
  /** Mutable ref kept in sync with `data`. Use inside callbacks to avoid stale closures. */
  dataRef: React.MutableRefObject<T[]>;
  /** Triggers a new auto-fetch by bumping an internal counter */
  refetch: () => void;
  /** Imperatively runs the query regardless of `enabled`. Returns a promise. */
  execute: () => Promise<void>;
}

function useElasticQueryClient<T = Record<string, unknown>>({
  queryFn,
  queryKey,
  enabled = true,
  keepPreviousData = false,
  onSuccess,
  onError,
}: UseElasticQueryOptions<T>): UseElasticQueryResult<T> {
  // Serialize queryKey once per render so the effect dep stays stable across
  // re-renders where the array reference changes but the values are the same.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const serializedKey = useMemo(() => JSON.stringify(queryKey), [JSON.stringify(queryKey)]);
  const [data, setData] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refetchCounter, setRefetchCounter] = useState(0);

  const dataRef = useRef<T[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Stable refs for callbacks and queryFn — avoids stale closures without
  // adding them to useEffect deps (which would cause infinite loops).
  const queryFnRef = useRef(queryFn);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  queryFnRef.current = queryFn;
  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;

  const runQuery = useCallback(async () => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsFetching(true);
    setError(null);

    if (!keepPreviousData) {
      dataRef.current = [];
      setData([]);
      setTotal(0);
    }

    try {
      const response = await queryFnRef.current(controller.signal);

      if (controller.signal.aborted) return;

      const items = response?.items ?? [];
      const count = response?.total ?? 0;

      dataRef.current = items;
      setData(items);
      setTotal(count);
      onSuccessRef.current?.(items, count);
    } catch (err) {
      if (controller.signal.aborted) return;

      const message = err instanceof Error ? err.message : 'An error occurred';
      setError(message);

      if (!keepPreviousData) {
        dataRef.current = [];
        setData([]);
        setTotal(0);
      }

      onErrorRef.current?.(message);
    } finally {
      if (!controller.signal.aborted) {
        setIsFetching(false);
      }
    }
  }, [keepPreviousData]);

  useEffect(() => {
    if (!enabled) {
      abortControllerRef.current?.abort();
      dataRef.current = [];
      setData([]);
      setTotal(0);
      setError(null);
      setIsFetching(false);
      return;
    }

    runQuery();

    return () => {
      abortControllerRef.current?.abort();
    };
    // queryFn is intentionally excluded — accessed via ref to prevent infinite loops.
    // serializedKey re-triggers the fetch when any queryKey value changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, refetchCounter, runQuery, serializedKey]);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const refetch = useCallback(() => {
    setRefetchCounter(c => c + 1);
  }, []);

  const execute = useCallback(async () => {
    await runQuery();
  }, [runQuery]);

  // loading = true only when fetching and there is no data to show yet.
  // With keepPreviousData=false this equals isFetching (data is cleared on each fetch).
  // With keepPreviousData=true this is only true on the very first fetch.
  const loading = isFetching && data.length === 0;

  return {
    data,
    total,
    loading,
    isFetching,
    error,
    dataRef,
    refetch,
    execute,
  };
}

export default useElasticQueryClient;
